import React, { useRef, useContext, useState, useEffect } from 'react';
import Data from '../data';
import Modal from '../modal';
import UI from '../ui';
import Utils from '../utils';
import LogoTypeEnum from './LogoType.enum';
import ThemeTypeEnum from './ThemeType.enum';
import Util from './Util';

const { ModalContext } = Modal;
const { Communication, Session } = Utils;
const { WrappedCheckbox, WrappedInputTextWithLabel, WrappedRadioButton, WrappedFileUpload } = UI;
const { Capability } = Data;

/**
 * Return one of the following of options:
 * 'default': logoType is 'image' and logoUrl is either Stamps logo or Endicia logo.
 * 'image': logoType is 'image' and logoUrl is from user's input
 * 'text': logoType is 'text'
 */
const calculateHeaderOption = (logoType, logoUrl) => {
	if (logoType === LogoTypeEnum.Image) {
		return logoUrl === Util.getDefaultLogoUrl() ? LogoTypeEnum.Default : LogoTypeEnum.Image;
	}

	return logoType;
};

const DesignTabPanel = props => {
	const { showError } = useContext(ModalContext);
	const {
		allowSmsOptIn,
		setBrandedTrackingConfig,
		setNotificationSettingsConfig,
		themeKey,
		storeName,
		storeUrl,
		logoType,
		logoUrl,
		logoName,
		tempLogoId,
		isValidationNeeded,
	} = props;
	const fileUploader = useRef(null);
	const [headerOption, setHeaderOption] = useState('');

	// WEBAPPS-29567, users must be enabled with Branded Tracking, Branded Emails and Branded SMS to use this feature.
	const canSeeBrandedSmsView =
		Session.hasCapability(Capability.BrandedEmails) &&
		Session.hasCapability(Capability.BrandedTracking) &&
		Session.hasCapability(Capability.BrandedTrackingSms);

	useEffect(() => {
		const option = calculateHeaderOption(logoType, logoUrl);
		setHeaderOption(option);
	}, [logoType, logoUrl]);

	const onFieldChange = async event => {
		const { value, name } = event.target;
		const newBrandingProperties = {
			[name]: value,
		};

		if (name === 'LogoType') {
			// set radio option
			setHeaderOption(value);
			newBrandingProperties.LogoName = '';

			if (value === LogoTypeEnum.Default) {
				newBrandingProperties.LogoType = LogoTypeEnum.Image; // default logo is a predefined 'image'
				newBrandingProperties.LogoUrl = Util.getDefaultLogoUrl();
			} else {
				newBrandingProperties.LogoUrl = '';
			}

			if (tempLogoId && tempLogoId !== '') {
				await Communication.deleteImage(tempLogoId);
				newBrandingProperties.TempLogoId = '';
			}
		}

		setBrandedTrackingConfig(newBrandingProperties);
	};

	const onCheckboxChange = ({ target }) => {
		const { name, checked } = target;

		setNotificationSettingsConfig({
			[name]: checked,
		});
	};

	const onLogoNameClick = () => {
		const inputList = document.querySelectorAll('.fileUploadContainer input[type=file]');

		if (inputList && inputList.length > 0) {
			inputList[0].click();
		}
	};
	// Process data using HTML5 FileReader API
	// and put base64 image and its name to state
	const onUpload = event => {
		const file = event.files[0];
		const { name, size } = file;
		const isFileTypeValid = Util.isFileTypeValid(name);
		const isFileSizeValid = Util.isFileSizeValid(size);

		if (!isFileTypeValid) {
			showError({
				data: {
					title: 'Invalid file type',
					description:
						'Invalid image type. Image must be one of the following file types: JPG, JPEG, PNG, GIF.',
					handleType: 'custom',
				},
			});
			fileUploader.current.clear();
			return;
		}

		if (!isFileSizeValid) {
			showError({
				data: {
					title: 'Invalid file size',
					description: 'Invalid image size. File size should be less than 2 MB',
					handleType: 'custom',
				},
			});
			fileUploader.current.clear();
			return;
		}
		const reader = new FileReader();

		reader.readAsDataURL(file);

		reader.onload = async e => {
			if (e.target.result) {
				const fileUploadFormData = new FormData();
				fileUploadFormData.append('file', file);

				const response = await Communication.uploadImage(
					fileUploadFormData,
					'BrandedTracking'
				);

				if (tempLogoId && tempLogoId !== '') {
					await Communication.deleteImage(tempLogoId);
				}

				if (Communication.isSuccessful(response)) {
					const { imageUrl, imageId } = response.data[0];

					setBrandedTrackingConfig({
						LogoUrl: imageUrl,
						LogoName: name,
						LogoId: imageId,
						TempLogoId: imageId,
					});
					fileUploader.current.clear();
				} else {
					showError({ response });

					if (fileUploader && fileUploader.current) {
						fileUploader.current.clear();
					}
				}
			} else {
				setBrandedTrackingConfig({ LogoUrl: '', LogoName: '' });

				if (fileUploader && fileUploader.current) {
					fileUploader.current.clear();
				}
			}
		};

		reader.onerror = e => {
			showError({
				data: {
					title: 'File Upload Error',
					description: `File can't be uploaded, due to: ${e.target.error.name}`,
					handleType: 'custom',
				},
			});

			if (fileUploader && fileUploader.current) {
				fileUploader.current.clear();
			}
		};
	};

	return (
		<div className="branded-tracking-design-tab-panel">
			<section className="pick-theme">
				<h2>Pick a Theme:</h2>
				<div className="theme-row">
					<WrappedRadioButton
						inputId="lightThemeKey"
						name="Theme"
						value={ThemeTypeEnum.Light}
						onChange={onFieldChange}
						dataqa="lightThemeKey"
						checked={themeKey === ThemeTypeEnum.Light}
					>
						Light
					</WrappedRadioButton>
					<WrappedRadioButton
						inputId="darkThemeKey"
						name="Theme"
						value={ThemeTypeEnum.Dark}
						onChange={onFieldChange}
						dataqa="darkThemeKey"
						checked={themeKey === ThemeTypeEnum.Dark}
					>
						Dark
					</WrappedRadioButton>
					<WrappedRadioButton
						inputId="peachThemeKey"
						name="Theme"
						value={ThemeTypeEnum.Peach}
						onChange={onFieldChange}
						dataqa="peachThemeKey"
						checked={themeKey === ThemeTypeEnum.Peach}
					>
						Peach
					</WrappedRadioButton>
				</div>
			</section>
			<WrappedInputTextWithLabel
				className="brand-input-wrapper"
				label="Brand Name:"
				isInputInvalid={isValidationNeeded && !storeName}
				errorMessage="Brand Name is required"
				showErrorBelow
				uiElementConfig={{
					value: storeName || '',
					onChange: onFieldChange,
					required: true,
					maxLength: 100,
					dataqa: 'BrandName',
					name: 'BrandName',
				}}
			/>
			<WrappedInputTextWithLabel
				className="brand-input-wrapper"
				label="Brand URL:"
				isInputInvalid={isValidationNeeded && Util.isLogoTargetUrlInvalid(storeUrl)}
				errorMessage={Util.getUrlValidationError(storeUrl)}
				showErrorBelow
				uiElementConfig={{
					value: storeUrl || '',
					placeholder: 'Opens when header is clicked',
					onChange: onFieldChange,
					maxLength: 100,
					dataqa: 'LogoTargetUrl',
					name: 'LogoTargetUrl',
				}}
			/>
			<section className="custom-header">
				<h2>Customize Header:</h2>
				<div>
					<WrappedRadioButton
						inputId="defaultLogoType"
						name="LogoType"
						value={LogoTypeEnum.Default}
						onChange={onFieldChange}
						dataqa="defaultLogoType"
						checked={headerOption === LogoTypeEnum.Default}
					>
						Use default
					</WrappedRadioButton>
					<WrappedRadioButton
						inputId="textLogoType"
						name="LogoType"
						value={LogoTypeEnum.Text}
						onChange={onFieldChange}
						dataqa="textLogoType"
						checked={headerOption === LogoTypeEnum.Text}
					>
						Use brand name
					</WrappedRadioButton>
					<WrappedRadioButton
						inputId="imageLogoType"
						name="LogoType"
						value={LogoTypeEnum.Image}
						onChange={onFieldChange}
						dataqa="imageLogoType"
						checked={headerOption === LogoTypeEnum.Image}
					>
						Use logo
					</WrappedRadioButton>
					<div className="file-upload-container">
						<WrappedInputTextWithLabel
							isInputInvalid={
								isValidationNeeded && Util.isLogoInvalid(logoType, logoUrl)
							}
							errorMessage="Image file is required in order to use logo"
							showErrorBelow
							className="logo-name"
							uiElementConfig={{
								value: logoName || '',
								readOnly: true,
								disabled: headerOption !== LogoTypeEnum.Image,
								onClick: onLogoNameClick,
								maxLength: 100,
								dataqa: 'LogoName',
								name: 'LogoName',
							}}
						/>
						<WrappedFileUpload
							ref={fileUploader}
							value={logoUrl || ''}
							disabled={headerOption !== LogoTypeEnum.Image}
							chooseLabel="Select File"
							accept=".jpg, .png, *.jpeg, *.gif"
							mode="basic"
							auto
							dataqa="LogoUrl"
							name="LogoUrl"
							customUpload
							uploadHandler={onUpload}
							className="fileUploadContainer"
						/>
					</div>
				</div>
			</section>
			{canSeeBrandedSmsView ? (
				<>
					<h2>Allow SMS Opt-In:</h2>
					<WrappedCheckbox
						className="allow-sms-opt-in"
						name="AllowSMSOptIn"
						onChange={onCheckboxChange}
						dataqa="allow-sms-opt-in"
						checked={allowSmsOptIn}
					>
						Allow recipients to receive SMS (Text) Tracking updates
					</WrappedCheckbox>
					<p className="sms-opt-in-note">
						NOTE: Branded SMS (text) Tracking Updates are available for no cost to you
						during this introductory period.
					</p>
				</>
			) : null}
		</div>
	);
};

export default DesignTabPanel;
